import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  XMarkIcon, 
  TruckIcon, 
  UserIcon, 
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Bus } from '../../types';
import { DateDisplay } from '../../components/Forms';

interface BusDetailsProps {
  busId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (bus: Bus) => void;
}

const BusDetails: React.FC<BusDetailsProps> = ({ busId, isOpen, onClose, onEdit }) => {
  const [driverWaBusy, setDriverWaBusy] = useState(false);
  const [driverWaMsg, setDriverWaMsg] = useState('');

  const { data: bus, isLoading, error } = useQuery({
    queryKey: ['bus', busId],
    queryFn: () => apiService.getBus(busId),
    enabled: isOpen && !!busId,
  });

  const handleNotifyDriver = async () => {
    if (!busId) return;
    setDriverWaBusy(true);
    setDriverWaMsg('');
    try {
      const res = await apiService.notifyBusDriver(busId);
      setDriverWaMsg(res.message || 'تم إرسال واتساب للسائق');
    } catch (err: any) {
      setDriverWaMsg(
        err?.response?.data?.message || err?.message || 'فشل إرسال واتساب للسائق'
      );
    } finally {
      setDriverWaBusy(false);
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'available': { label: 'متاح', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      'on-trip': { label: 'في رحلة', color: 'bg-blue-100 text-blue-800', icon: TruckIcon },
      'maintenance': { label: 'صيانة', color: 'bg-yellow-100 text-yellow-800', icon: WrenchScrewdriverIcon },
      'out-of-service': { label: 'خارج الخدمة', color: 'bg-red-100 text-red-800', icon: XCircleIcon }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      color: 'bg-gray-100 text-gray-800', 
      icon: ClockIcon 
    };
    
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {statusInfo.label}
      </span>
    );
  };

  const getMaintenanceStatus = (bus: Bus) => {
    if (!bus.maintenance.nextService) return null;
    
    const today = new Date();
    const nextService = new Date(bus.maintenance.nextService);
    const daysDiff = Math.ceil((nextService.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
          صيانة متأخرة ({Math.abs(daysDiff)} يوم)
        </span>
      );
    }
    
    if (daysDiff <= 7) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <ClockIcon className="w-4 h-4 mr-2" />
          صيانة مستحقة خلال {daysDiff} يوم
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-4 h-4 mr-2" />
        صيانة محدثة
      </span>
    );
  };

  const getDriverLicenseStatus = (bus: Bus) => {
    if (!bus.driver.licenseExpiry) return null;
    
    const today = new Date();
    const expiryDate = new Date(bus.driver.licenseExpiry);
    const daysDiff = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
          رخصة منتهية الصلاحية
        </span>
      );
    }
    
    if (daysDiff <= 30) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <ClockIcon className="w-4 h-4 mr-2" />
          تنتهي خلال {daysDiff} يوم
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-4 h-4 mr-2" />
        رخصة سارية
      </span>
    );
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-center">جاري تحميل بيانات الحافلة...</p>
        </div>
      </div>
    );
  }

  if (error || !bus) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
          <h3 className="text-lg font-medium text-red-800 mb-2">خطأ في تحميل البيانات</h3>
          <p className="text-red-600 mb-4">لم يتم العثور على تفاصيل الحافلة</p>
          <button onClick={onClose} className="btn-primary">
            إغلاق
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl mx-auto mt-8 mb-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <TruckIcon className="w-6 h-6 text-primary-600 ml-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                تفاصيل الحافلة {bus.busNumber}
              </h3>
              <p className="text-sm text-gray-500">{bus.plateNumber}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 space-x-reverse">
            {bus.driver?.phone && (
              <button
                type="button"
                onClick={handleNotifyDriver}
                disabled={driverWaBusy}
                className="inline-flex items-center gap-1.5 btn-secondary text-emerald-700 disabled:opacity-50"
                title="إرسال واتساب للسائق"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                {driverWaBusy ? 'جاري الإرسال...' : 'واتساب السائق'}
              </button>
            )}
            <button
              onClick={() => onEdit(bus)}
              className="btn-secondary"
            >
              تعديل
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {driverWaMsg && (
            <p
              className={`text-sm rounded-lg px-3 py-2 ${
                driverWaMsg.includes('فشل') || driverWaMsg.includes('لا يوجد')
                  ? 'bg-red-50 text-red-700'
                  : 'bg-emerald-50 text-emerald-800'
              }`}
            >
              {driverWaMsg}
            </p>
          )}

          {/* Status Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">حالة الحافلة</h4>
              {getStatusBadge(bus.status)}
              {!bus.isActive && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    غير نشط
                  </span>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">حالة الصيانة</h4>
              {getMaintenanceStatus(bus)}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">رخصة السائق</h4>
              {getDriverLicenseStatus(bus)}
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <TruckIcon className="w-5 h-5 text-primary-600 ml-2" />
                المعلومات الأساسية
              </h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">رقم الحافلة</label>
                  <p className="text-sm text-gray-900">{bus.busNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">رقم اللوحة</label>
                  <p className="text-sm text-gray-900">{bus.plateNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">السعة</label>
                  <p className="text-sm text-gray-900">{bus.capacity} مقعد</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">الفرع</label>
                  <p className="text-sm text-gray-900">{bus.branch.name}</p>
                </div>
              </div>
              
              {bus.model && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">معلومات المركبة</h5>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">الماركة</label>
                      <p className="text-sm text-gray-900">{bus.model.brand || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">الموديل</label>
                      <p className="text-sm text-gray-900">{bus.model.model || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">سنة الصنع</label>
                      <p className="text-sm text-gray-900">{bus.model.year || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">اللون</label>
                      <p className="text-sm text-gray-900">{bus.model.color || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Driver Information */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <UserIcon className="w-5 h-5 text-primary-600 ml-2" />
                معلومات السائق
              </h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">الاسم</label>
                  <p className="text-sm text-gray-900">{bus.driver.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">رقم الجوال</label>
                  <p className="text-sm text-gray-900">{bus.driver.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">رقم الرخصة</label>
                  <p className="text-sm text-gray-900">{bus.driver.licenseNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">تاريخ انتهاء الرخصة</label>
                  <p className="text-sm text-gray-900"><DateDisplay value={bus.driver.licenseExpiry} /></p>
                </div>
                {bus.driver.experience && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">سنوات الخبرة</label>
                    <p className="text-sm text-gray-900">{bus.driver.experience} سنة</p>
                  </div>
                )}
                {bus.driver.idNumber && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">رقم الهوية</label>
                    <p className="text-sm text-gray-900">{bus.driver.idNumber}</p>
                  </div>
                )}
              </div>
              {bus.driver?.phone && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleNotifyDriver}
                    disabled={driverWaBusy}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    {driverWaBusy ? 'جاري الإرسال...' : 'إرسال واتساب للسائق'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Supervisor Information */}
          {bus.supervisor && bus.supervisor.name && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <UserIcon className="w-5 h-5 text-primary-600 ml-2" />
                  معلومات المشرف
                </h4>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">الاسم</label>
                    <p className="text-sm text-gray-900">{bus.supervisor.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">رقم الجوال</label>
                    <p className="text-sm text-gray-900">{bus.supervisor.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">رقم الهوية</label>
                    <p className="text-sm text-gray-900">{bus.supervisor.idNumber || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features and Routes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Features */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-lg font-medium text-gray-900">المميزات</h4>
              </div>
              <div className="p-4">
                {bus.features && bus.features.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {bus.features.map((feature) => (
                      <span key={feature} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {feature}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">لا توجد مميزات محددة</p>
                )}
              </div>
            </div>

            {/* Routes */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPinIcon className="w-5 h-5 text-primary-600 ml-2" />
                  المسارات المتاحة
                </h4>
              </div>
              <div className="p-4">
                {bus.routes && bus.routes.length > 0 ? (
                  <div className="space-y-2">
                    {bus.routes.map((route) => (
                      <span key={route} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                        {route}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">لا توجد مسارات محددة</p>
                )}
              </div>
            </div>
          </div>

          {/* Pricing and Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pricing */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <CurrencyDollarIcon className="w-5 h-5 text-primary-600 ml-2" />
                  التسعير
                </h4>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">التكلفة لكل كيلومتر</span>
                  <span className="text-sm font-medium text-gray-900">{bus.pricing.costPerKM} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">الأجرة اليومية</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(bus.pricing.dailyRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">استهلاك الوقود</span>
                  <span className="text-sm font-medium text-gray-900">{bus.pricing.fuelConsumption} لتر/100كم</span>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200">
                <h4 className="text-lg font-medium text-gray-900">الإحصائيات</h4>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">إجمالي الرحلات</span>
                  <span className="text-sm font-medium text-gray-900">{bus.stats.totalTrips}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">إجمالي الإيرادات</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(bus.stats.totalRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">متوسط الإشغال</span>
                  <span className="text-sm font-medium text-gray-900">{Math.round(bus.stats.averageOccupancy)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">إجمالي الكيلومترات</span>
                  <span className="text-sm font-medium text-gray-900">{bus.stats.totalMileage.toLocaleString('en-US')} كم</span>
                </div>
                {bus.stats.lastTripDate && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">آخر رحلة</span>
                    <span className="text-sm font-medium text-gray-900"><DateDisplay value={bus.stats.lastTripDate} /></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Maintenance Information */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <WrenchScrewdriverIcon className="w-5 h-5 text-primary-600 ml-2" />
                معلومات الصيانة
              </h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">الكيلومترات الحالية</label>
                  <p className="text-sm text-gray-900">{bus.maintenance.mileage.toLocaleString('en-US')} كم</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">فترة الصيانة</label>
                  <p className="text-sm text-gray-900">{bus.maintenance.serviceInterval.toLocaleString('en-US')} كم</p>
                </div>
                {bus.maintenance.lastService && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">آخر صيانة</label>
                    <p className="text-sm text-gray-900"><DateDisplay value={bus.maintenance.lastService} /></p>
                  </div>
                )}
                {bus.maintenance.nextService && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">الصيانة القادمة</label>
                    <p className="text-sm text-gray-900"><DateDisplay value={bus.maintenance.nextService} /></p>
                  </div>
                )}
              </div>
              
              {bus.maintenance.notes && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <label className="text-sm font-medium text-gray-500">ملاحظات الصيانة</label>
                  <p className="text-sm text-gray-900 mt-1">{bus.maintenance.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusDetails; 